import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { activateAccount } from "../services/store/authentication.actions";
import { LoadingSpinnerButton } from "../../_UI/Components/PathwayButtons";
import { useParams } from "react-router";
import styled from "styled-components";
import { ErrorMessage } from "../../_UI/Components/Alerts/PathwayAlertMessages";
import PathwayTextInputGroup from "../../_UI/Components/Inputs/PathwayTextInputGroup";


const ActivationScreen = () => {
    const dispatch = useDispatch();
    const { id } = useParams();

    const activationUUID = useMemo(() => {
        if (id && id !== "") {
            return id;
        } else {
            return null;
        }
    }, [id]);

    const authentication = useSelector((state) => state.authentication);

    const [newPassword, setNewPassword] = useState("");
    const [newPasswordRepeat, setNewPasswordRepeat] = useState("");

    const [submitted, setSubmitted] = useState(false);

    const handleNewPasswordChange = useCallback((e) => {
        setNewPassword(e.target.value);
    }, []);

    const handleNewPasswordRepeatChange = useCallback((e) => {
        setNewPasswordRepeat(e.target.value);
    }, []);

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            setSubmitted(true);
            if (activationUUID && newPassword && newPasswordRepeat) {
                dispatch(activateAccount(activationUUID, newPassword, newPasswordRepeat));
            }
        },
        [dispatch, activationUUID, newPassword, newPasswordRepeat]
    );

    return (
        <SplitScreenContainer>
            <LeftSection>
                <LeftLogoContainer>
                    <img
                        src="/logo_black.png"
                        alt="Pathway Logo"
                        style={{ maxWidth: "200px", marginBottom: "2rem" }}
                        className="rounded mx-auto d-block"
                    />
                </LeftLogoContainer>
                <HeaderText>
                    Das manuelle Buchen
überspringen
                </HeaderText>
                <LegalFooter>
                    <LegalLink
                        href="https://www.pathway-solutions.de/impressum"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Impressum
                    </LegalLink>
                    <LegalSeparator>•</LegalSeparator>
                    <LegalLink
                        href="https://app.pathway-solutions.de/Nutzungsvereinbarung_Platform.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Nutzungsvereinbarung
                    </LegalLink>
                    <LegalSeparator>•</LegalSeparator>
                    <LegalLink
                        href="https://www.pathway-solutions.de/data-privacy-webapp"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Datenschutz
                    </LegalLink>
                </LegalFooter>
            </LeftSection>

            <RightSection>
                <ActivationFormContainer>
                   
                    <PageTitle>Bitte wähle dein Passwort, um die Registrierung abzuschließen</PageTitle>
                     <SubtitleText>
                        Alternativ kannst Du die Aktivierung direkt im <a href="/login">Anmeldebildschirm</a> über Google oder Microsoft vornehmen.
                    </SubtitleText>

                    {activationUUID ? (
                        <ActivationContent>
                            {authentication.errorMessage && (
                                <ErrorMessage marginless>{authentication.errorMessage}</ErrorMessage>
                            )}

                            <InputFieldContainer>
                                <FieldLabel>Neues Passwort</FieldLabel>
                                <PathwayTextInputGroup
                                    type="password"
                                    name="newPassword"
                                    placeHolder="Dein neues Passwort"
                                    value={newPassword}
                                    handleChange={handleNewPasswordChange}
                                    submitted={submitted}
                                />
                                {submitted && !newPassword && (
                                    <ValidationError>Passwort ist ein Pflichtfeld</ValidationError>
                                )}
                            </InputFieldContainer>

                            <InputFieldContainer>
                                <FieldLabel>Passwort wiederholen</FieldLabel>
                                <PathwayTextInputGroup
                                    type="password"
                                    name="newPasswordRepeat"
                                    placeHolder="Passwort wiederholen"
                                    value={newPasswordRepeat}
                                    handleChange={handleNewPasswordRepeatChange}
                                    submitted={submitted}
                                />
                                {submitted && (!newPassword || !newPasswordRepeat) && (
                                    <ValidationError>Passwort ist ein Pflichtfeld</ValidationError>
                                )}
                            </InputFieldContainer>

                            <PasswordRequirements>
                                <RequirementTitle>Passwort-Anforderungen:</RequirementTitle>
                                <RequirementsList>
                                    <RequirementItem>mindestens 12 Zeichen</RequirementItem>
                                    <RequirementItem>mindestens 1 Großbuchstabe</RequirementItem>
                                    <RequirementItem>mindestens 1 Zahl</RequirementItem>
                                    <RequirementItem>darf nur folgende Sonderzeichen enthalten: ?,!,_,*,.,@</RequirementItem>
                                </RequirementsList>
                            </PasswordRequirements>

                            <ButtonContainer>
                                <LoadingSpinnerButton
                                    loading={authentication && authentication.loading}
                                    disabled={authentication && authentication.loading}
                                    onClick={handleSubmit}
                                >
                                    Passwort setzen
                                </LoadingSpinnerButton>
                            </ButtonContainer>
                        </ActivationContent>
                    ) : (
                        <ErrorContainer>
                            <ErrorMessage marginless>Dieser Link ist ungültig</ErrorMessage>
                        </ErrorContainer>
                    )}

                    <CompanyFooter>
                        <CompanyName>pathway solutions gmbh</CompanyName>
                    </CompanyFooter>
                </ActivationFormContainer>
            </RightSection>
        </SplitScreenContainer>
    );
};

const SplitScreenContainer = styled.div`
    display: flex;
    height: 100vh;
    width: 100vw;
    
    @media (max-width: 768px) {
        flex-direction: column;
        height: auto;
        min-height: 100vh;
    }
`;

const LeftSection = styled.div`
    flex: 1;
    background: #EFFBFF;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    
    @media (max-width: 768px) {
        flex: none;
        padding: 1.5rem 1rem;
        min-height: 40vh;
        justify-content: flex-start;
        order: 2; /* Move to bottom on mobile */
    }
`;

const LeftLogoContainer = styled.div`
    margin-bottom: 2rem;
    
    @media (max-width: 768px) {
        margin-bottom: 1rem;
    }
`;

const HeaderText = styled.h2`
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 600;
    text-align: center;
    color: #1f2937;
    margin-bottom: 50px;
    line-height: 1.2;
    
    @media (max-width: 768px) {
        font-size: 22px;
        margin-bottom: 1.5rem;
        margin-top: 1rem;
    }
`;

const RightSection = styled.div`
    flex: 1;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    
    @media (max-width: 768px) {
        flex: none;
        padding: 1rem;
        align-items: flex-start;
        min-height: 60vh;
        order: 1; /* Move to top on mobile */
    }
`;

const ActivationFormContainer = styled.div`
    width: 100%;
    max-width: 400px;
    
    @media (max-width: 768px) {
        max-width: 100%;
        padding: 0;
    }
`;

const PageTitle = styled.h1`
    font-family: 'Inter', sans-serif;
    font-size: 24px;
    font-weight: 600;
    text-align: center;
    color: #1f2937;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
        font-size: 20px;
        margin-bottom: 0.75rem;
    }
`;

const ActivationContent = styled.div`
    display: grid;
    row-gap: 1.5rem;
    margin-top: 2rem;
    
    @media (max-width: 768px) {
        row-gap: 1rem;
        margin-top: 1.5rem;
    }
`;

const PasswordRequirements = styled.div`
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
        padding: 0.75rem;
        margin-bottom: 0.75rem;
    }
`;

const RequirementTitle = styled.h4`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 0.5rem 0;
    
    @media (max-width: 768px) {
        font-size: 13px;
        margin: 0 0 0.375rem 0;
    }
`;

const RequirementsList = styled.ul`
    margin: 0;
    padding-left: 1.2rem;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: #6B7280;
    
    @media (max-width: 768px) {
        font-size: 12px;
        padding-left: 1rem;
    }
`;

const RequirementItem = styled.li`
    margin-bottom: 0.25rem;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const InputFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    
    input {
        height: 45px !important;
    }
    
    @media (max-width: 768px) {
        gap: 0.375rem;
        
        input {
            height: 44px !important;
        }
    }
`;

const FieldLabel = styled.label`
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    color: #1f2937;
    font-weight: 500;
    text-align: left;
    
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const ValidationError = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #dc2626;
    margin-top: 0.25rem;
`;

const ButtonContainer = styled.div`
    margin-top: 0px;
    
    button {
        height: 45px;
        font-size: 16px;
    }
    
    @media (max-width: 768px) {
        button {
            height: 44px;
            font-size: 15px;
        }
    }
`;

const ErrorContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 2rem;
`;

const CompanyFooter = styled.div`
    margin-top: 3rem;
    text-align: center;
    
    @media (max-width: 768px) {
        margin-top: 2rem;
    }
`;

const CompanyName = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #6B7280;
    text-align: center;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
        font-size: 13px;
    }
`;

const LegalFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-top: 2rem;
    padding: 1rem;
`;

const LegalLink = styled.a`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #6B7280;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
        color: #374151;
        text-decoration: underline;
    }
    
    &:focus {
        outline: none;
        color: #374151;
    }
`;

const LegalSeparator = styled.span`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #9CA3AF;
`;

const SubtitleText = styled.p`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    text-align: center;
    color: #6B7280;
    margin-bottom: 1rem;
    line-height: 1.5;
    
    @media (max-width: 768px) {
        margin-bottom: 2rem;
        font-size: 13px;
    }
`;

export default ActivationScreen;
